export const DEFAULT_WORK_ORDER_REGISTERS = Object.freeze([
  { name: 'combined_id', type: 'u32', address: 4000, words: 2, access: 'read' },
  { name: 'job_name', type: 'ascii', address: 4002, words: 6, access: 'read' },
  { name: 'material_type', type: 'u32', address: 4008, words: 2, access: 'read' },
  { name: 'material_shape', type: 'u32', address: 4010, words: 2, access: 'read' },
  { name: 'material_width', type: 'u32', address: 4012, words: 2, access: 'read' },
  { name: 'material_height', type: 'u32', address: 4014, words: 2, access: 'read' },
  { name: 'work_sequence', type: 'u32', address: 4016, words: 2, access: 'read' },
  { name: 'input_length', type: 'u32', address: 4018, words: 2, access: 'read' },
  { name: 'input_num', type: 'u32', address: 4020, words: 2, access: 'read' },
  { name: 'work_num', type: 'u32', address: 4022, words: 2, access: 'read' },
  { name: 'cutting_speed', type: 'u32', address: 4024, words: 2, access: 'read' },
  { name: 'saw_blade_num', type: 'u32', address: 4026, words: 2, access: 'read' },
  { name: 'saw_blade_rpm', type: 'u32', address: 4028, words: 2, access: 'read' },
  { name: 'message_code', type: 'u32', address: 4030, words: 2, access: 'read' },
  { name: 'cutting_volume', type: 'u16', address: 4032, words: 1, access: 'read' },
  { name: 'on_off_signal', type: 'u16', address: 4033, words: 1, access: 'read' },
  { name: 'hour', type: 'u32', address: 4034, words: 2, access: 'read' },
  { name: 'minute', type: 'u32', address: 4036, words: 2, access: 'read' },
  { name: 'job_name', type: 'ascii', address: 3500, words: 10, access: 'write' },
  { name: 'material_type', type: 'u32', address: 3510, words: 2, access: 'write' },
  { name: 'material_width', type: 'u32', address: 3512, words: 2, access: 'write' },
  { name: 'material_height', type: 'u32', address: 3514, words: 2, access: 'write' },
  { name: 'material_shape', type: 'u32', address: 3516, words: 2, access: 'write' },
  { name: 'input_length', type: 'u32', address: 3518, words: 2, access: 'write' },
  { name: 'input_num', type: 'u32', address: 3520, words: 2, access: 'write' },
  { name: 'urgent', type: 'u16', address: 3522, words: 1, access: 'write' },
].map((register) => Object.freeze({ ...register, divisor: 1 })));

export const DEFAULT_COMPLETION_PAYLOAD = Object.freeze([
  { name: 'company_name', type: 'ascii', address: 4044, words: 6, divisor: 1 },
  { name: 'cutting_length', type: 'u32', address: 4050, words: 2, divisor: 1 },
  { name: 'input_num', type: 'u32', address: 4052, words: 2, divisor: 1 },
  { name: 'cutting_num', type: 'u32', address: 4054, words: 2, divisor: 1 },
  { name: 'material_type', type: 'u32', address: 4056, words: 2, divisor: 1 },
  { name: 'material_shape', type: 'u32', address: 4058, words: 2, divisor: 1 },
  { name: 'material_size1', type: 'u32', address: 4060, words: 2, divisor: 1 },
  { name: 'material_size2', type: 'u32', address: 4062, words: 2, divisor: 1 },
  { name: 'remaining_material', type: 'u32', address: 4064, words: 2, divisor: 1 },
].map((field) => Object.freeze(field)));

export const createDefaultGatewayPLCCompletionConfig = (saved = null) => {
  const source = saved || {};
  const signal = source.signal || {};
  return {
    messageEnabled: source.messageEnabled ?? true,
    signalEnabled: source.signalEnabled ?? false,
    signal: {
      address: Number(signal.address ?? 4042), type: signal.type || 'u32', words: Number(signal.words ?? 2),
      onValue: Number(signal.onValue ?? 1), resetValue: Number(signal.resetValue ?? 0), resetOwner: signal.resetOwner || 'gateway',
    },
    payload: DEFAULT_COMPLETION_PAYLOAD.map((canonical) => {
      const field = Array.isArray(source.payload) ? source.payload.find((item) => item.name === canonical.name) : null;
      return {
        ...canonical,
        address: Number(field?.address ?? canonical.address),
        words: canonical.words,
        divisor: Number(field?.divisor ?? canonical.divisor),
      };
    }),
  };
};

export const SCALABLE_READ_REGISTERS = Object.freeze(new Set([
  'material_width', 'material_height', 'input_length',
  'cutting_speed', 'saw_blade_rpm', 'cutting_volume',
]));

const exactIPv4Pattern = /^(?:0|[1-9]\d{0,2})(?:\.(?:0|[1-9]\d{0,2})){3}$/;
const isIntegerInRange = (value, min, max) => {
  if (value === '' || value === null || value === undefined) return false;
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max;
};

export const validateGatewayPLCConfig = (form) => {
  const errors = [];
  const host = String(form.host ?? '').trim();
  if (!exactIPv4Pattern.test(host) || host.split('.').some((octet) => Number(octet) > 255)) errors.push('IPv4');
  if (!isIntegerInRange(form.port, 1, 65535)) errors.push('TCP Port');
  if (!isIntegerInRange(form.unitID, 1, 247)) errors.push('Unit ID');
  if (!isIntegerInRange(form.pollMs, 100, 60000)) errors.push('Polling');
  if (!isIntegerInRange(form.timeoutMs, 100, 60000)) errors.push('Timeout');
  if (!isIntegerInRange(form.baseOffset, 0, 65535)) errors.push('Base Offset');

  const completion = form.completionConfig;
  const signal = completion?.signal;
  let completionInvalid = !completion || signal?.type !== 'u32' || Number(signal?.words) !== 2
    || !isIntegerInRange(signal?.address, 0, 65534) || Number(signal?.onValue) !== 1
    || Number(signal?.resetValue) !== 0 || signal?.resetOwner !== 'gateway'
    || (Boolean(completion?.messageEnabled) && Boolean(completion?.signalEnabled));
  const completionRanges = [];
  const payload = Array.isArray(completion?.payload) ? completion.payload : [];
  if (payload.length !== DEFAULT_COMPLETION_PAYLOAD.length) completionInvalid = true;
  payload.forEach((field, index) => {
    const canonical = DEFAULT_COMPLETION_PAYLOAD[index];
    if (!canonical || field?.name !== canonical.name || field?.type !== canonical.type || Number(field?.words) !== canonical.words
      || !isIntegerInRange(field?.address, 0, 65535) || Number(field.address) + Number(field.words) - 1 > 65535) completionInvalid = true;
    const divisor = Number(field?.divisor);
    if (field?.type === 'ascii' ? divisor !== 1 : ![1, 10, 100, 1000].includes(divisor)) completionInvalid = true;
    if (field && isIntegerInRange(field.address, 0, 65535) && isIntegerInRange(field.words, 1, 65536)) {
      completionRanges.push({ start: Number(field.address), end: Number(field.address) + Number(field.words) - 1 });
    }
  });
  completionRanges.sort((a, b) => a.start - b.start);
  if (completionRanges.some((range, index) => index > 0 && range.start <= completionRanges[index - 1].end)) completionInvalid = true;
  if (signal && isIntegerInRange(signal.address, 0, 65534)) {
    const signalStart = Number(signal.address); const signalEnd = signalStart + 1;
    if (completionRanges.some((range) => range.start <= signalEnd && signalStart <= range.end)) completionInvalid = true;
  }
  if (completionInvalid) errors.push('completionConfig');

  const registers = Array.isArray(form.registers) ? form.registers : [];
  if (registers.length !== DEFAULT_WORK_ORDER_REGISTERS.length) errors.push('26');
  let rowsComplete = registers.length === DEFAULT_WORK_ORDER_REGISTERS.length;
  const ranges = [];
  registers.forEach((register, index) => {
    const canonical = DEFAULT_WORK_ORDER_REGISTERS[index];
    const complete = Boolean(register && String(register.name ?? '').trim() && String(register.type ?? '').trim()
      && String(register.access ?? '').trim())
      && isIntegerInRange(register.address, 0, 65535)
      && isIntegerInRange(register.words, 1, 65536);
    if (!complete) rowsComplete = false;
    if (!canonical || !register || register.name !== canonical.name || register.type !== canonical.type
      || register.access !== canonical.access || Number(register.words) !== canonical.words) {
      errors.push('type/words');
    }
    const divisor = Number(register?.divisor ?? 1);
    const scalable = register?.access === 'read' && SCALABLE_READ_REGISTERS.has(register?.name);
    if (![1, 10, 100, 1000].includes(divisor) || (!scalable && divisor !== 1)) errors.push('divisor');
    if (complete) {
      const start = Number(form.baseOffset) + Number(register.address);
      const end = start + Number(register.words) - 1;
      if (start < 0 || end > 65535) errors.push('65535');
      ranges.push({ start, end, access: register.access });
    }
  });
  if (!rowsComplete) errors.push('complete');
  ['read', 'write'].forEach((access) => {
    const accessRanges = ranges.filter((range) => range.access === access).sort((a, b) => a.start - b.start);
    if (accessRanges.some((range, index) => index > 0 && range.start <= accessRanges[index - 1].end)) errors.push('overlap');
  });
  return [...new Set(errors)];
};

export const buildGatewayPLCConfigPayload = (form) => ({
  companyID: form.companyID,
  machineID: Number(form.machineID),
  enabled: Boolean(form.enabled),
  protocol: form.protocol,
  mode: form.mode,
  host: form.host.trim(),
  port: Number(form.port),
  unitID: Number(form.unitID),
  profileName: form.profileName.trim(),
  byteOrder: form.byteOrder,
  asciiByteOrder: form.asciiByteOrder,
  wordSwap: Boolean(form.wordSwap),
  pollMs: Number(form.pollMs),
  timeoutMs: Number(form.timeoutMs),
  writeEnabled: Boolean(form.writeEnabled),
  completionConfig: {
    messageEnabled: Boolean(form.completionConfig.messageEnabled),
    signalEnabled: Boolean(form.completionConfig.signalEnabled),
    signal: {
      address: Number(form.completionConfig.signal.address), type: form.completionConfig.signal.type,
      words: Number(form.completionConfig.signal.words), onValue: Number(form.completionConfig.signal.onValue),
      resetValue: Number(form.completionConfig.signal.resetValue), resetOwner: form.completionConfig.signal.resetOwner,
    },
    payload: form.completionConfig.payload.map((field) => ({
      name: field.name, type: field.type, address: Number(field.address), words: Number(field.words), divisor: Number(field.divisor),
    })),
  },
  memoryMap: {
    baseOffset: Number(form.baseOffset),
    registers: form.registers.map((register) => ({
      name: register.name,
      type: register.type,
      address: Number(register.address),
      words: Number(register.words),
      access: register.access,
      divisor: Number(register.divisor ?? 1),
    })),
  },
});
